import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  auditLogsProjectGroupsItemSheet,
  changeToNonSmfProductId,
  changeToSmfProductId,
  createProjectGroupsItemSheetFinalized,
  createProjectGroupsItemSheetFromFile,
  createProjectGroupsItemSheetFromOriginal,
  createProjectGroupsItemSheets,
  deleteProjectGroupsItemSheetFinalized,
  deleteProjectGroupsItemSheetLatest,
  downloadProjectGroupsItemSheet,
  duplicateProjectGroupsItemSheetLatest,
  overwriteProjectGroupsItemSheetFromFile,
  readProjectGroupsItemSheet,
  readProjectGroupsItemSheetFinalized,
  readProjectGroupsItemSheetLatest,
  readItemsheetInProgressCount,
  readProjectGroupsItemSheetOriginalInfo,
  readProjectGroupsItemSheetOriginalList,
  restartItemsheetOriginal,
  searchSMF,
  updateProjectGroupsItemSheetLatest,
  highlightProjectGroupsItemsheet,
} from "@/services/api/itemsheet";
import {
  AuditLogsProjectGroupsItemSheetRequest,
  ChangeToNonSmfProductProjectGroupsItemsheetRequest,
  ChangeToSmfProductProjectGroupsItemsheetRequest,
  CreateProjectGroupsItemSheetFinalizedRequest,
  CreateProjectGroupsItemSheetFromFileRequest,
  CreateProjectGroupsItemSheetFromOriginalRequest,
  CreateProjectGroupsItemSheetRequest,
  DeleteProjectGroupsItemSheetFinalizedRequest,
  DeleteProjectGroupsItemSheetLatestRequest,
  DownloadProjectGroupsItemSheetRequest,
  DuplicateProjectGroupsItemSheetLatestRequest,
  OverwriteProjectGroupsItemSheetFromFileRequest,
  ReadProjectGroupsItemSheetLatestRequest,
  ReadProjectGroupsItemSheetOriginalInfoRequest,
  ReadProjectGroupsItemSheetRequest,
  RestartItemsheetOriginalRequest,
  SearchSmfRequest,
  UpdateProjectGroupsItemSheetLatestRequest,
  HighlightProjectGroupsItemsheetRequest,
} from "@/types/itemsheet";
import { QUERY_KEYS } from "../queryKeys";
import { ProjectType } from "@/consts/Project";
import { ItemSheetStatus } from "@/consts/Estimate";

// 산출내역서 확정 생성
export const useCreateProjectGroupsItemSheetFinalized = (group_id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectGroupsItemSheetFinalizedRequest) =>
      createProjectGroupsItemSheetFinalized({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.FINALIZED(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
          group_id,
          variables.original_id
        ),
      });
    },
  });
};

// 산출내역서 확정 조회
export const useReadProjectGroupsItemSheetFinalized = ({
  group_id,
  project_type,
}: {
  group_id: string;
  project_type: ProjectType;
}) => {
  const data = { project_type };
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.FINALIZED(group_id),
    queryFn: () => readProjectGroupsItemSheetFinalized({ group_id, data }),
    enabled: !!group_id && !!project_type,
  });
};

// 산출내역서 확정 삭제
export const useDeleteProjectGroupsItemSheetFinalized = (group_id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DeleteProjectGroupsItemSheetFinalizedRequest) =>
      deleteProjectGroupsItemSheetFinalized({ group_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.FINALIZED(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.ROOT(group_id),
      });
    },
  });
};

// 산출내역서 원본 정보 조회 (단일)
export const useReadProjectGroupsItemSheetOriginalInfo = (
  group_id: string,
  data: ReadProjectGroupsItemSheetOriginalInfoRequest
) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.ORIGINAL_INFO(
      group_id,
      data.project_id
    ),
    queryFn: () => readProjectGroupsItemSheetOriginalInfo({ group_id, data }),
    enabled: !!group_id && !!data.project_id && !!data.project_type,
  });
};

// 산출내역서 원본 목록 조회
// 생성 진행 중(REQUESTED, PROCESSING)인 원본이 있으면 5초마다 폴링
export const useReadProjectGroupsItemSheetOriginalList = (group_id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.ORIGINAL_LIST(group_id),
    queryFn: () => readProjectGroupsItemSheetOriginalList({ group_id }),
    enabled: !!group_id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const originals = [
        ...(data.proj_originals ?? []),
        ...(data.prcr_originals ?? []),
      ];
      const inProgress = originals.some(
        (original) =>
          original.status === ItemSheetStatus.REQUESTED ||
          original.status === ItemSheetStatus.PROCESSING
      );
      return inProgress ? 5000 : false;
    },
  });
};

// 산출내역서 생성 진행 중 개수 조회 (생성 전 화면에서만 마운트되어 10초마다 폴링)
export const useReadItemsheetInProgressCount = (group_id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.IN_PROGRESS_COUNT(group_id),
    queryFn: () => readItemsheetInProgressCount({ group_id }),
    enabled: !!group_id,
    refetchInterval: 10000,
    staleTime: 5000,
  });
};

// 산출내역서 생성 재시작
export const useRestartItemsheetOriginal = (group_id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RestartItemsheetOriginalRequest) =>
      restartItemsheetOriginal({ group_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.ORIGINAL_LIST(group_id),
      });
    },
  });
};

// 산출내역서 최신본(latest) 생성 API (파일 업로드)
export const useCreateProjectGroupsItemSheetFromFile = (group_id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectGroupsItemSheetFromFileRequest) =>
      createProjectGroupsItemSheetFromFile({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
          group_id,
          variables.original_id
        ),
      });
    },
  });
};

// 산출내역서 최신본(latest) 덮어쓰기 API (파일 업로드)
export const useOverwriteProjectGroupsItemSheetFromFile = (
  group_id: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      latest_sheet_id,
      data,
    }: {
      latest_sheet_id: string;
      data: OverwriteProjectGroupsItemSheetFromFileRequest;
    }) =>
      overwriteProjectGroupsItemSheetFromFile({
        group_id,
        latest_sheet_id,
        data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
          group_id,
          variables.data.original_id
        ),
      });
    },
  });
};

// 산출내역서 최신본(latest) 생성 API (원본 복사)
export const useCreateProjectGroupsItemSheetFromOriginal = (
  group_id: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectGroupsItemSheetFromOriginalRequest) =>
      createProjectGroupsItemSheetFromOriginal({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
          group_id,
          variables.original_id
        ),
      });
    },
  });
};

// 산출내역서 원본 및 최신본(latest) 생성
export const useCreateProjectGroupsItemSheets = (group_id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectGroupsItemSheetRequest) =>
      createProjectGroupsItemSheets({ group_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.ROOT(group_id),
      });
    },
  });
};

// 산출내역서 최신 버전 목록 조회
export const useReadProjectGroupsItemSheet = (
  group_id: string,
  data: ReadProjectGroupsItemSheetRequest
) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
      group_id,
      data.original_id
    ),
    queryFn: () => readProjectGroupsItemSheet({ group_id, data }),
    enabled:
      !!group_id &&
      !!data.project_id &&
      !!data.project_type &&
      !!data.original_id,
  });
};

// 산출내역서 최신 버전 조회
export const useReadProjectGroupsItemSheetLatest = (
  group_id: string,
  latest_sheet_id: string,
  data: ReadProjectGroupsItemSheetLatestRequest
) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST(
      group_id,
      latest_sheet_id
    ),
    queryFn: () =>
      readProjectGroupsItemSheetLatest({ group_id, latest_sheet_id, data }),
    enabled:
      !!group_id &&
      !!latest_sheet_id &&
      !!data.project_id &&
      !!data.project_type &&
      !!data.original_id,
  });
};

// 산출내역서 최신 버전 수정
export const useUpdateProjectGroupsItemSheetLatest = (
  group_id: string,
  latest_sheet_id: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProjectGroupsItemSheetLatestRequest) =>
      updateProjectGroupsItemSheetLatest({ group_id, latest_sheet_id, data }),

    onMutate: async (variables) => {
      const { title, original_id } = variables;

      if (title) {
        const latestListKey = QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
          group_id,
          original_id
        );

        await queryClient.cancelQueries({ queryKey: latestListKey });

        const previous = queryClient.getQueryData(latestListKey);

        queryClient.setQueryData(latestListKey, (old: any) => {
          if (!old?.latests) return old;

          return {
            ...old,
            latests: old.latests.map((item: any) =>
              item.id === latest_sheet_id
                ? { ...item, title: variables.title }
                : item
            ),
          };
        });

        return { previous, queryKey: latestListKey };
      }

      return null;
    },

    onError: (_err, _variables, context) => {
      if (context?.previous && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },

    onSuccess: (_data, variables) => {
      const { data } = variables;

      if (data) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST(
            group_id,
            latest_sheet_id
          ),
        });
      }

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.AUDIT_LOGS(group_id),
      });
    },
  });
};

// 산출내역서 최신 버전 삭제
export const useDeleteProjectGroupsItemSheetLatest = (group_id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      latest_sheet_id,
      data,
    }: {
      latest_sheet_id: string;
      data: DeleteProjectGroupsItemSheetLatestRequest;
    }) =>
      deleteProjectGroupsItemSheetLatest({ group_id, latest_sheet_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
          group_id,
          variables.data.original_id
        ),
      });
    },
  });
};

// 산출내역서 최신 버전 복사
export const useDuplicateProjectGroupsItemSheetLatest = (group_id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      latest_sheet_id,
      data,
    }: {
      latest_sheet_id: string;
      data: DuplicateProjectGroupsItemSheetLatestRequest;
    }) =>
      duplicateProjectGroupsItemSheetLatest({
        group_id,
        latest_sheet_id,
        data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST_LIST(
          group_id,
          variables.data.original_id
        ),
      });
    },
  });
};

// 산출내역서 엑셀 파일 다운로드
export const useDownloadProjectGroupsItemSheet = (
  group_id: string,
  latest_sheet_id: string
) => {
  return useMutation({
    mutationFn: (data: DownloadProjectGroupsItemSheetRequest) =>
      downloadProjectGroupsItemSheet({ group_id, latest_sheet_id, data }),
  });
};

// 산출내역서 감사 로그 조회
export const useAuditLogsProjectGroupsItemSheet = (
  group_id: string,
  data: AuditLogsProjectGroupsItemSheetRequest
) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.AUDIT_LOGS(group_id),
    queryFn: () => auditLogsProjectGroupsItemSheet({ group_id, data }),
    enabled: !!group_id && !!data.project_id && !!data.project_type,
  });
};

// 산출내역서 자사제품(SMF) 제품 ID 변경
export const useChangeToSmfProductId = (
  group_id: string,
  latest_sheet_id: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ChangeToSmfProductProjectGroupsItemsheetRequest) =>
      changeToSmfProductId({
        group_id,
        latest_sheet_id,
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST(
          group_id,
          latest_sheet_id
        ),
      });
    },
  });
};

// 산출내역서 타사 제품으로 제품ID 변경
export const useChangeToTppProductId = (
  group_id: string,
  latest_sheet_id: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ChangeToNonSmfProductProjectGroupsItemsheetRequest) =>
      changeToNonSmfProductId({
        group_id,
        latest_sheet_id,
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST(
          group_id,
          latest_sheet_id
        ),
      });
    },
  });
};

// 산출내역서 하이라이트 적용
export const useHighlightProjectGroupsItemsheet = (
  group_id: string,
  latest_sheet_id: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: HighlightProjectGroupsItemsheetRequest) =>
      highlightProjectGroupsItemsheet({
        group_id,
        latest_sheet_id,
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST(
          group_id,
          latest_sheet_id
        ),
      });
    },
  });
};

// 자사제품(SMF) 검색
export const useSearchSmf = (data: SearchSmfRequest) => {
  return useQuery({
    queryKey: QUERY_KEYS.SMF.SEARCH(data.product_id, data.item_type),
    queryFn: () => searchSMF(data),
    enabled: false,
  });
};
